/* Sep-2026 · ConfirmDialog — shadcn-native replacement for
 * `window.confirm` and `window.prompt`. Two modes:
 *
 *   <ConfirmDialog trigger={{ ...call ConfirmDialog.openConfirm() }}
 *     title="Delete?" description="This cannot be undone."
 *     onConfirm={fn} />
 *
 * OR use the imperative helpers:
 *
 *   await openConfirm({ title, description, tone: 'destructive' });   // → bool
 *   const reason = await openPrompt({ title, placeholder, min: 3 });  // → string|null
 *
 * Both helpers mount a single portal-hosted dialog per call. The
 * dialog inherits the current `data-admin-theme` so Fluent styling
 * carries through automatically.
 */
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';


function _mountRoot() {
  const host = document.createElement('div');
  host.setAttribute('data-cnr-confirm-root', '');
  document.body.appendChild(host);
  const root = createRoot(host);
  const unmount = () => {
    setTimeout(() => { root.unmount(); host.remove(); }, 250);
  };
  return { root, unmount };
}


/** Imperative confirm — resolves true (Confirm) or false (Cancel). */
export function openConfirm({
  title = 'Are you sure?',
  description = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',        // 'default' | 'destructive'
} = {}) {
  const { root, unmount } = _mountRoot();
  return new Promise((resolve) => {
    function View() {
      const [open, setOpen] = useState(true);
      const finish = (v) => {
        setOpen(false);
        resolve(v);
        unmount();
      };
      return (
        <AlertDialog open={open} onOpenChange={(o) => !o && finish(false)}>
          <AlertDialogContent data-testid="cnr-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{title}</AlertDialogTitle>
              {description && (
                <AlertDialogDescription>{description}</AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => finish(false)} data-testid="cnr-confirm-cancel">
                {cancelLabel}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => finish(true)}
                data-testid="cnr-confirm-ok"
                className={cn(tone === 'destructive' &&
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90')}>
                {confirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    }
    root.render(<View />);
  });
}


/** Imperative prompt — resolves the trimmed string or null (Cancel). */
export function openPrompt({
  title = 'Enter a value',
  description = '',
  placeholder = '',
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  minLength = 0,
  initial = '',
  inputType = 'text',   // Feb-2026 · honor `password` so browser autocap
                          // / autocorrect / spellcheck don't silently
                          // rewrite what the admin typed.
} = {}) {
  const { root, unmount } = _mountRoot();
  return new Promise((resolve) => {
    function View() {
      const [open, setOpen] = useState(true);
      const [val, setVal] = useState(initial);
      const finish = (v) => {
        setOpen(false);
        resolve(v);
        unmount();
      };
      const submit = () => {
        // Feb-2026 · Passwords must NOT be trimmed — a legitimate
        // password can contain leading/trailing whitespace. Everything
        // else trims to prevent stray spaces from breaking IDs/emails.
        const raw = val || '';
        const finalVal = inputType === 'password' ? raw : raw.trim();
        if (finalVal.length < minLength) return;   // block until valid
        finish(finalVal);
      };
      return (
        <AlertDialog open={open} onOpenChange={(o) => !o && finish(null)}>
          <AlertDialogContent data-testid="cnr-prompt-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{title}</AlertDialogTitle>
              {description && (
                <AlertDialogDescription>{description}</AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <div className="py-2">
              <Input
                autoFocus
                type={inputType}
                autoCapitalize={inputType === 'password' ? 'off' : undefined}
                autoCorrect={inputType === 'password' ? 'off' : undefined}
                spellCheck={inputType === 'password' ? false : undefined}
                autoComplete={inputType === 'password' ? 'new-password' : undefined}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder={placeholder}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                  if (e.key === 'Escape') finish(null);
                }}
                data-testid="cnr-prompt-input"
              />
              {minLength > 0 && val && val.length < minLength && (
                <div className="mt-1 text-[11px] text-destructive">
                  Minimum {minLength} characters.
                </div>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => finish(null)} data-testid="cnr-prompt-cancel">
                {cancelLabel}
              </AlertDialogCancel>
              <AlertDialogAction onClick={submit} data-testid="cnr-prompt-ok">
                {confirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    }
    root.render(<View />);
  });
}
